// ---------------------------------------------------------------------------
// SaveToLibraryDialog — saves the current 6-axis design system to the
// user's library via the platform.cogo.xyz Worker. Replaces v6.5
// SaveToAppDialog (which wrote to apps/<id>/design-system.json — the
// architecturally backwards Save flow that was reverted in Phase 600).
// ---------------------------------------------------------------------------

import { useState } from "react";
import { Save, X } from "lucide-react";
import { useDesignSystem } from "../hooks/use-design-system";
import { useStudioLocale } from "@/i18n/use-studio-locale";
import { saveDesignSystemToLibrary } from "../lib/save-design-system-library";

interface SaveToLibraryDialogProps {
  triggerClassName?: string;
}

type Status =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "error"; message: string }
  | { kind: "success"; message: string };

export function SaveToLibraryDialog({ triggerClassName }: SaveToLibraryDialogProps) {
  const { designSystem } = useDesignSystem();
  const { strings } = useStudioLocale();
  const copy = strings.designSystem.workbench;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const close = () => {
    setOpen(false);
    setName("");
    setDescription("");
    setStatus({ kind: "idle" });
  };

  async function handleSave() {
    if (!name.trim()) {
      setStatus({ kind: "error", message: copy.nameRequired });
      return;
    }
    setStatus({ kind: "saving" });
    const result = await saveDesignSystemToLibrary({
      name: name.trim(),
      description: description.trim(),
      tokens: designSystem,
    });
    if (result.success && result.data) {
      setStatus({
        kind: "success",
        message: copy.savedAs.replace("{name}", result.data.name),
      });
      setTimeout(close, 1200);
    } else {
      setStatus({ kind: "error", message: result.error ?? copy.saveFailed });
    }
  }

  return (
    <>
      <button
        type="button"
        className={
          triggerClassName ??
          "inline-flex items-center gap-2 px-3 py-2 text-sm rounded border hover:bg-muted"
        }
        onClick={() => setOpen(true)}
      >
        <Save className="h-4 w-4" />
        {copy.saveToLibrary}
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={close}
        >
          <div
            className="w-[420px] rounded-lg bg-background p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">{copy.saveDialogTitle}</h2>
              <button onClick={close} aria-label={copy.closeLabel}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <input
              type="text"
              placeholder={copy.namePlaceholder}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full mb-3 rounded border px-3 py-2"
              autoFocus
            />
            <textarea
              placeholder={copy.descriptionPlaceholder}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full mb-4 rounded border px-3 py-2 text-sm"
            />
            {status.kind === "error" && (
              <div className="text-sm text-destructive mb-3">{status.message}</div>
            )}
            {status.kind === "success" && (
              <div className="text-sm text-success mb-3">{status.message}</div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={close} className="px-3 py-2 text-sm">
                {copy.cancel}
              </button>
              <button
                onClick={handleSave}
                disabled={status.kind === "saving"}
                className="px-3 py-2 text-sm bg-primary text-primary-foreground rounded"
              >
                {status.kind === "saving" ? copy.saving : copy.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
